import { create } from 'zustand'

const AI_ROOMS_KEY = 'chatpulse_ai_rooms'
const MUTED_ROOMS_KEY = 'chatpulse_muted_rooms'

export const ACCENT_THEMES = ['cyan', 'violet', 'emerald', 'rose', 'amber', 'blue', 'indigo', 'teal', 'light'] as const
export type AccentTheme = (typeof ACCENT_THEMES)[number]

interface UIState {
  theme: 'light' | 'dark'
  accentTheme: AccentTheme
  sidebarOpen: boolean
  memberPanelOpen: boolean
  threadsViewOpen: boolean
  aiEnabledRooms: Set<string>
  mutedRooms: Set<string>

  toggleTheme: () => void
  setAccentTheme: (theme: AccentTheme) => void
  setSidebarOpen: (open: boolean) => void
  setMemberPanelOpen: (open: boolean) => void
  setThreadsViewOpen: (open: boolean) => void
  toggleAiForRoom: (roomId: string) => void
  isAiEnabledForRoom: (roomId: string) => boolean
  toggleMuteRoom: (roomId: string) => void
  isRoomMuted: (roomId: string) => boolean
}

function getInitialTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light'
  const stored = localStorage.getItem('chatpulse_theme')
  if (stored === 'dark') return 'dark'
  return 'light'
}

function applyTheme(theme: 'light' | 'dark') {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  localStorage.setItem('chatpulse_theme', theme)
}

function getInitialAccent(): AccentTheme {
  if (typeof window === 'undefined') return 'cyan'
  const stored = localStorage.getItem('chatpulse_accent')
  return (ACCENT_THEMES as readonly string[]).includes(stored ?? '') ? (stored as AccentTheme) : 'cyan'
}

function applyAccent(theme: AccentTheme) {
  const el = document.documentElement
  ACCENT_THEMES.forEach((t) => el.classList.remove(`theme-${t}`))
  if (theme !== 'cyan') el.classList.add(`theme-${theme}`)
  localStorage.setItem('chatpulse_accent', theme)
}

function loadAiRooms(): Set<string> {
  try {
    const raw = localStorage.getItem(AI_ROOMS_KEY)
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch { /* ignore */ }
  return new Set()
}

function saveAiRooms(rooms: Set<string>) {
  localStorage.setItem(AI_ROOMS_KEY, JSON.stringify([...rooms]))
}

function loadMutedRooms(): Set<string> {
  try {
    const raw = localStorage.getItem(MUTED_ROOMS_KEY)
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch { /* ignore */ }
  return new Set()
}

function saveMutedRooms(rooms: Set<string>) {
  localStorage.setItem(MUTED_ROOMS_KEY, JSON.stringify([...rooms]))
}

const initial = getInitialTheme()
applyTheme(initial)
const initialAccent = getInitialAccent()
applyAccent(initialAccent)

export const useUIStore = create<UIState>((set, get) => ({
  theme: initial,
  accentTheme: initialAccent,
  sidebarOpen: true,
  memberPanelOpen: false,
  threadsViewOpen: false,
  aiEnabledRooms: loadAiRooms(),
  mutedRooms: loadMutedRooms(),

  toggleTheme: () =>
    set((state) => {
      const next = state.theme === 'light' ? 'dark' : 'light'
      applyTheme(next)
      return { theme: next }
    }),

  setAccentTheme: (theme) => {
    applyAccent(theme)
    set({ accentTheme: theme })
  },

  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setMemberPanelOpen: (open) => set({ memberPanelOpen: open }),
  setThreadsViewOpen: (open) => set({ threadsViewOpen: open }),

  toggleAiForRoom: (roomId) =>
    set((state) => {
      const rooms = new Set(state.aiEnabledRooms)
      if (rooms.has(roomId)) rooms.delete(roomId)
      else rooms.add(roomId)
      saveAiRooms(rooms)
      return { aiEnabledRooms: rooms }
    }),

  isAiEnabledForRoom: (roomId) => get().aiEnabledRooms.has(roomId),

  toggleMuteRoom: (roomId) =>
    set((state) => {
      const rooms = new Set(state.mutedRooms)
      if (rooms.has(roomId)) rooms.delete(roomId)
      else rooms.add(roomId)
      saveMutedRooms(rooms)
      return { mutedRooms: rooms }
    }),

  isRoomMuted: (roomId) => get().mutedRooms.has(roomId),
}))
